import { IMG } from "@/lib/site";
import Reveal from "@/components/site/Reveal";
import SectionCTA from "@/components/site/SectionCTA";
import FAQ from "@/components/site/FAQ";
import useSeo from "@/lib/useSeo";
import { useJsonLd, buildMedicalConditionSchema } from "@/lib/useJsonLd";
import { useFaqs } from "@/lib/faqs";
import { useT } from "@/lib/i18n";

const Fibromialgia = () => {
  const t = useT();
  const { fibro } = useFaqs();
  useSeo({
    title: {
      es: "Aliviar Fatiga Crónica y Fibromialgia · Acompañamiento Online | Método TAO",
      ca: "Alleujar Fatiga Crònica i Fibromiàlgia · Acompanyament Online | Mètode TAO",
    },
    description: {
      es: "Cómo aliviar la fatiga crónica y la fibromialgia desde un enfoque cuerpo-mente. Yoga restaurativo, respiración y regulación del sistema nervioso para fatiga, dolor difuso e insomnio. Sesiones online en español y presenciales en Barcelona.",
      ca: "Com alleujar la fatiga crònica i la fibromiàlgia des d'un enfocament cos-ment. Ioga restauratiu, respiració i regulació del sistema nerviós per a fatiga, dolor difús i insomni. Sessions online i presencials a Barcelona.",
    },
    path: "/fibromialgia",
  });
  // FAQ schema is injected by the <FAQ> component itself — do NOT duplicate here.
  useJsonLd("condition-fibromialgia", buildMedicalConditionSchema({
    name: "Fibromialgia y fatiga crónica",
    alternateName: ["Síndrome de fatiga crónica", "Encefalomielitis miálgica", "Dolor crónico", "Sensibilización central"],
    description: "Acompañamiento cuerpo-mente para vivir con fibromialgia y fatiga crónica: yoga restaurativo, regulación del sistema nervioso, respiración y sueño.",
    url: "https://metodo-tao.com/fibromialgia",
  }));

  const items = [
    { t: t("fibro.item1.t"), d: t("fibro.item1.d") },
    { t: t("fibro.item2.t"), d: t("fibro.item2.d") },
    { t: t("fibro.item3.t"), d: t("fibro.item3.d") },
    { t: t("fibro.item4.t"), d: t("fibro.item4.d") },
    { t: t("fibro.item5.t"), d: t("fibro.item5.d") },
  ];

  return (
  <div className="bg-ivory">
    <section className="pt-12 md:pt-20 pb-16 md:pb-24" data-testid="page-fibromialgia">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <Reveal className="lg:col-span-7">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">{t("fibro.eyebrow")}</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal leading-[0.98]">
            {t("fibro.title1")} <span className="italic text-terracotta">{t("fibro.title2")}</span>.
          </h1>
          <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-xl">
            {t("fibro.lead")}
          </p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="aspect-[4/5] rounded-tr-[4rem] rounded-bl-[4rem] overflow-hidden bg-sand">
            <img src={IMG.selfCompassionBW} alt="Regulación del sistema nervioso · fibromialgia y fatiga crónica" className="w-full h-full object-cover" fetchPriority="high" decoding="async" />
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-20 md:py-28 bg-charcoal text-ivory grain">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-[11px] tracking-[0.32em] uppercase text-terracotta mb-5">{t("fibro.helpsEyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">
            {t("fibro.helpsTitle1")} <span className="italic text-terracotta">{t("fibro.helpsTitle2")}</span>.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-12 gap-6">
          {items.map((i, idx) => (
            <Reveal key={i.t} delay={idx * 0.06} className="md:col-span-6 lg:col-span-4">
              <article className="bg-ivory/5 border border-ivory/10 rounded-3xl p-8 h-full">
                <span className="text-[11px] tracking-[0.3em] text-terracotta">0{idx + 1}</span>
                <h3 className="mt-4 font-serif text-2xl md:text-3xl">{i.t}</h3>
                <p className="mt-3 text-ivory/70 text-[15px] leading-relaxed">{i.d}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <FAQ
      eyebrow={t("fibro.faqEyebrow")}
      title={t("fibro.faqTitle")}
      items={fibro}
    />

    <SectionCTA title={t("fibro.ctaTitle")} body={t("fibro.ctaBody")} />
  </div>
  );
};

export default Fibromialgia;
